import * as React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';

import { Icon } from './Icon';

export interface ModalProps {
  open?: boolean;
  title?: React.ReactNode;
  onClose?: () => void;
  size?: 'md' | 'lg' | 'xl';
  /** Custom card width in px — overrides `size`. */
  width?: number;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}

const CARD_WIDTHS = { md: '480px', lg: '640px', xl: '1200px' } as const;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: grid;
  place-items: center;
  z-index: 900;
  padding: 24px;
  animation: lumen-fade-in ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.ease};
`;
const Card = styled.div<{ $size: 'md' | 'lg' | 'xl'; $width?: number }>`
  background: ${({ theme }) => theme.color.bg.default};
  border-radius: ${({ theme }) => theme.radius.base};
  box-shadow: ${({ theme }) => theme.shadow.xl};
  width: min(
    ${({ $size, $width }) => ($width ? `${$width}px` : CARD_WIDTHS[$size])},
    100%
  );
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: lumen-modal-in 200ms ${({ theme }) => theme.motion.ease};
`;
const Head = styled.div`padding: 20px 24px 12px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;`;
const Title = styled.h3`font-size: ${({ theme }) => theme.fontSize.lg}; font-weight: ${({ theme }) => theme.fontWeight.bold}; letter-spacing: -0.01em; margin: 0;`;
const CloseBtn = styled.button`
  border: 0; background: none; cursor: pointer;
  color: ${({ theme }) => theme.color.fg.subtle};
  width: 28px; height: 28px; border-radius: ${({ theme }) => theme.radius.sm};
  display: grid; place-items: center; flex-shrink: 0;
  &:hover { background: ${({ theme }) => theme.color.bg.muted}; color: ${({ theme }) => theme.color.fg.default}; }
`;
const Body = styled.div`padding: 0 24px 20px; color: ${({ theme }) => theme.color.fg.muted}; font-size: ${({ theme }) => theme.fontSize.base}; flex: 1; min-height: 0; overflow: auto;`;
const Foot = styled.div`padding: 12px 24px 20px; display: flex; justify-content: flex-end; gap: 8px;`;

export function Modal({ open = true, title, onClose, size = 'md', width, footer, children }: ModalProps) {
  if (!open) return null;
  return createPortal(
    <Backdrop>
      <Card $size={size} $width={width} role="dialog" aria-modal="true">
        <Head>
          <Title>{title}</Title>
          {onClose && <CloseBtn onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></CloseBtn>}
        </Head>
        <Body>{children}</Body>
        {footer && <Foot>{footer}</Foot>}
      </Card>
    </Backdrop>,
    document.body
  );
}
